import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CartouchesService, Cartouche, CartouchePayload } from '../../../../services/cartouches.service';
import { Chimie, ChimieService } from '../../../../services/chimie.service';
import { LotCartouche, LotsCartouchesService } from '../../../../services/lots-cartouches.service';

@Component({
	selector: 'app-cartouche-edit',
	standalone: true,
	imports: [CommonModule, FormsModule, MatAutocompleteModule, MatOptionModule],
	templateUrl: './cartouche-edit.component.html',
	styleUrl: './cartouche-edit.component.scss'
})
export class CartoucheEditComponent implements OnInit {
	form: CartouchePayload = {
		nom: '',
		lot_cartouche_id: undefined,
		chimie_id: undefined,
		utilise: false,
		inutilisable: false
	};
	error = '';
	success = '';
	loading = false;
	id: number | null = null;
	cartoucheSearch = '';
	filteredCartouches: Cartouche[] = [];
	lots: LotCartouche[] = [];
	chimies: Chimie[] = [];

	constructor(
		private readonly cartouchesService: CartouchesService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly lotsCartouchesService: LotsCartouchesService,
		private readonly chimieService: ChimieService,
		private readonly cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.lotsCartouchesService.list().subscribe({
			next: (lots: LotCartouche[]) => { this.lots = lots; this.cdr.detectChanges(); },
			error: () => { this.lots = []; this.cdr.detectChanges(); }
		});
		this.chimieService.list().subscribe({
			next: (chimies: Chimie[]) => { this.chimies = chimies; this.cdr.detectChanges(); },
			error: () => { this.chimies = []; this.cdr.detectChanges(); }
		});
		this.cartouchesService.list().subscribe({
			next: (cartouches: Cartouche[]) => {
				this.filteredCartouches = cartouches;
				this.cdr.detectChanges();
			},
			error: () => {
				this.filteredCartouches = [];
				this.cdr.detectChanges();
			}
		});
		this.route.queryParams.subscribe((params: any) => {
			this.id = params['id'] ? Number(params['id']) : null;
			this.loadSelectedCartouche();
		});
	}

	onCartoucheSearchChange(value: string): void {
		this.id = null;
		const searchValue = value ? value.trim() : '';
		
		if (searchValue.length >= 1) {
			// Recherche avec au moins 1 caractère
			this.cartouchesService.autocompleteNom(searchValue).subscribe((cartouches: Cartouche[]) => {
				this.filteredCartouches = cartouches;
				this.cdr.detectChanges();
			});
		} else if (searchValue.length === 0) {
			// Champ vide, afficher les 10 premiers
			this.cartouchesService.autocompleteNom('').subscribe((cartouches: Cartouche[]) => {
				this.filteredCartouches = cartouches;
				this.cdr.detectChanges();
			});
		}
	}

	displayCartouche(value: Cartouche | string | null): string {
		if (!value) {
			return '';
		}

		return typeof value === 'string' ? value : (value.nom ?? '');
	}

	onCartoucheSelected(event: any): void {
		const cartouche = event.option.value;
		this.cartoucheSearch = cartouche.nom ?? '';
		this.id = cartouche.id;
		this.router.navigate([], { queryParams: { id: this.id }, queryParamsHandling: 'merge' });
		this.loadSelectedCartouche();
	}

	private loadSelectedCartouche(): void {
		if (this.id) {
			this.cartouchesService.get(this.id).subscribe({
				next: (cartouche: Cartouche) => {
					this.form = { ...cartouche };
					this.cartoucheSearch = cartouche.nom ?? '';
					this.cdr.detectChanges();
				},
				error: () => {
					this.error = 'Erreur lors du chargement de la cartouche.';
					this.cdr.detectChanges();
				}
			});
		} else {
			this.form = {
				nom: '',
				lot_cartouche_id: undefined,
				chimie_id: undefined,
				utilise: false,
				inutilisable: false
			};
			this.cdr.detectChanges();
		}
	}

	update(): void {
		if (!this.id) return;
		this.error = '';
		this.success = '';
		this.loading = true;
		this.cartouchesService.update(this.id, this.form).subscribe({
			next: (updatedCartouche: Cartouche) => {
				this.form = { ...updatedCartouche };
				this.cartoucheSearch = updatedCartouche.nom ?? '';
				this.filteredCartouches = this.filteredCartouches.map(cartouche =>
					cartouche.id === updatedCartouche.id ? updatedCartouche : cartouche
				);
				this.success = 'Cartouche modifiée.';
				this.loading = false;
				this.cdr.detectChanges();
			},
			error: (err: HttpErrorResponse) => {
				this.error = this.getErrorMessage(err, 'Erreur lors de la modification.');
				this.loading = false;
				this.cdr.detectChanges();
			}
		});
	}

	private getErrorMessage(err: HttpErrorResponse, fallback: string): string {
		if (typeof err.error === 'string' && err.error.trim()) {
			return err.error;
		}
		if (err.error?.detail) {
			return err.error.detail;
		}
		if (err.error?.message) {
			return err.error.message;
		}
		if (err.message) {
			return err.message;
		}
		return fallback;
	}
}